import { computed, effect, Injectable, signal } from '@angular/core'
import { toObservable } from '@angular/core/rxjs-interop'
import { IsoDateTime } from '@naturalcycles/js-lib'
import {
  AccountTM,
  CartState,
  DiscountCodeFM,
  GoalBreakpointOption,
  HardwareSelection,
  LANG,
  Order,
  PaymentState,
  ProductKey,
  ProductState,
  PublicAndPrivateKeyTransport,
  QuizDataInput,
  RemoteConfigWeb,
  USD,
  UserLocale,
} from '@naturalcycles/shared'
import { ConsentTerms } from '@naturalcycles/shared/src/model/consentRecord.shared.model'
import { ExperimentState } from '@naturalcycles/shared/src/model/experiment.shared.model'
import { SubscriptionStateFM } from '@naturalcycles/shared/src/model/subscription.shared.model'
import { FlowDefinitionName } from '@src/app/constants/flows.js'
import { sessionStorageService, storageService } from '@src/app/core/services/storage.service.js'
import { sentry } from '@src/app/core/util/sentry.util.js'
import { ModalInterface } from '@src/app/shared/typings/interfaces/modals.js'
import { QuizState, SignupQuizFlow } from '@src/app/shared/typings/interfaces/quiz.js'
import { debounceTime, distinctUntilChanged } from 'rxjs/operators'
import { Path } from '../services/flow.service.js'
import { emptyCartState } from '../util/cart.util.js'
import { filterAssignmentsForAllowedLanguages } from '../util/experiment.util.js'

const STATE = 'signupState'
const SESSION_STATE = 'signupSessionState'

// Reference to the initialized SignalStore
let signalStore: SignalStore

// shortcut
export function getSignalStore(): SignalStore {
  return signalStore
}

export function getState(): SignalState {
  return signalStore.getState()
}

export interface SignalState extends LocalStorageState, SessionStorageState {
  loading: boolean
  ghost: boolean
  bootstrapComplete: boolean
  lang: LANG
  langLoaded: boolean
  userLocale: UserLocale
  modals: ModalInterface[]
  remoteConfig: RemoteConfigWeb
  consent: ConsentTerms
  payment: PaymentState | null
  experiment: ExperimentState
}

interface LocalStorageState {
  sessionId?: string
  sessionEncryptionKeys?: PublicAndPrivateKeyTransport
  hardware: HardwareSelection // todo: does it need to be persistent?
  rawExperiment: ExperimentState
  subscriptions: SubscriptionStateFM
  order: Order
  product: ProductState
  quiz: QuizState
  cart: CartState
  account: AccountTM
  flow: Partial<Record<FlowDefinitionName, Path[]>>
}

interface SessionStorageState {
  discountCode?: DiscountCodeFM
  referral?: string
  qpFertilityGoal?: GoalBreakpointOption
  isEmailVerificationFlow?: boolean

  // skippedDiscountedHardwareKey are intended for churned users who want to subscribe again through a discount code.
  // If the code is configured correctly, we send users from secret page straight to manage account.
  // The user might already have a thermometer/B1 from the previous subscription, so she'll have the option to not get another one.
  skippedDiscountedHardwareKey?: ProductKey

  // Red dot features
  overrideTime?: IsoDateTime
  /**
   * API URL override. When set it will be used instead of the default API URL configured in the environment.
   */
  overrideApiUrl?: string
}

@Injectable({ providedIn: 'root' })
export class SignalStore {
  readonly $account = signal<AccountTM>({} as AccountTM)
  readonly $loading = signal(false)
  readonly $ghost = signal(false)
  readonly $bootstrapComplete = signal(false)
  readonly $lang = signal<LANG>('' as LANG)
  readonly $langLoaded = signal(false)
  readonly $userLocale = signal({} as UserLocale)
  readonly $modals = signal<ModalInterface[]>([])
  readonly $sessionId = signal<string | undefined>(undefined)
  readonly $sessionEncryptionKeys = signal<PublicAndPrivateKeyTransport | undefined>(undefined)
  readonly $remoteConfig = signal<RemoteConfigWeb>({})
  readonly $hardware = signal({} as HardwareSelection)
  readonly $consent = signal<ConsentTerms>({} as ConsentTerms)
  readonly $rawExperiment = signal<ExperimentState>({} as ExperimentState)
  readonly $experiment = computed(() => {
    const rawExperiment = this.$rawExperiment()
    const lang = this.$lang()
    if (!lang) {
      return {
        ...rawExperiment,
        assignments: {},
      }
    }
    return {
      ...rawExperiment,
      assignments: filterAssignmentsForAllowedLanguages(rawExperiment.assignments, lang),
    }
  })

  readonly $payment = signal<PaymentState | null>(null)
  readonly $subscriptions = signal<SubscriptionStateFM>({} as SubscriptionStateFM)
  readonly $order = signal<Order>({} as Order)
  readonly $product = signal<ProductState>({} as ProductState)
  readonly $quiz = signal<QuizState>({} as QuizState)
  readonly $cart = signal<CartState>(emptyCartState(USD))
  readonly $flow = signal<Record<FlowDefinitionName, Path[]>>(
    {} as Record<FlowDefinitionName, Path[]>,
  )

  // Session storage variables
  readonly $qpFertilityGoal = signal<GoalBreakpointOption | undefined>(undefined)
  readonly $discountCode = signal<DiscountCodeFM | undefined>(undefined)
  readonly $referral = signal<string | undefined>(undefined)
  readonly $isEmailVerificationFlow = signal<boolean | undefined>(undefined)
  readonly $overrideTime = signal<IsoDateTime | undefined>(undefined)
  readonly $overrideApiUrl = signal<string | undefined>(undefined)
  readonly $skippedDiscountedHardwareKey = signal<ProductKey | undefined>(undefined)

  constructor() {
    signalStore = this

    this.reset()
    this.loadStateFromLocalStorage()
    this.loadStateFromSessionStorage()

    effect(() => {
      const { country } = this.$userLocale()
      if (!country) return
      sentry.setTags({ country })
    })

    // Persisting state to LocalStorage
    toObservable(computed(() => this.getLocalStorageState()))
      .pipe(
        debounceTime(1000),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
      )
      .subscribe(() => this.persistToLocalStorage())

    // Persisting state to SessionStorage
    toObservable(computed(() => this.getSessionStorageState()))
      .pipe(
        debounceTime(1000),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
      )
      .subscribe(() => this.persistToSessionStorage())
  }

  // ensures that constructor is called and signalStore variable is initialized
  init(): void {}

  /**
   * Resets the state to its initial values.
   * Useful e.g in unit tests.
   */
  reset(): void {
    this.$account.set({} as AccountTM)
    this.$loading.set(false)
    this.$ghost.set(false)
    this.$bootstrapComplete.set(false)
    this.$lang.set('' as LANG)
    this.$langLoaded.set(false)
    this.$userLocale.set({} as UserLocale)
    this.$modals.set([])
    this.$sessionId.set(undefined)
    this.$sessionEncryptionKeys.set(undefined)
    this.$remoteConfig.set({
      recaptchaEnabled: true,
      googleAuthEnabled: true,
    })
    this.$hardware.set({
      // // null means "not loaded"
      hwId: null,
      /*
       * ownsDevice property could theoretically be removed.
       * As a single source of truth, we could derive it from the cart state.
       * Eg. const ownsOuraRing = cartItems.find(item => item.key === ProductKey.OURA_RING_DISCOUNT) && item.variant === ProductVariant.ownsDevice
       */
      ownsDevice: null,
    } as any as HardwareSelection)
    this.$consent.set({} as ConsentTerms)
    this.$rawExperiment.set({
      id: '',
      assignments: {},
    } as ExperimentState)
    this.$payment.set(null)
    this.$subscriptions.set({} as SubscriptionStateFM)
    this.$order.set({
      cart: emptyCartState(USD),
    })
    this.$product.set({ items: [] })
    this.$quiz.set({
      flow: [],
      data: null,
      sections: null,
      signature: null,
      entry: null,
      exit: null,
    })
    this.$cart.set(emptyCartState(USD))
    this.$flow.set({
      [FlowDefinitionName.SIGNUP]: [],
      [FlowDefinitionName.CLEAR_BLUE]: [],
    })

    // Session storage variables
    this.$discountCode.set(undefined)
    this.$referral.set(undefined)
    this.$qpFertilityGoal.set(undefined)
    this.$isEmailVerificationFlow.set(undefined)
    this.$overrideTime.set(undefined)
    this.$skippedDiscountedHardwareKey.set(undefined)
  }

  getState(): SignalState {
    return {
      loading: this.$loading(),
      ghost: this.$ghost(),
      bootstrapComplete: this.$bootstrapComplete(),
      lang: this.$lang(),
      langLoaded: this.$langLoaded(),
      userLocale: this.$userLocale(),
      modals: this.$modals(),
      remoteConfig: this.$remoteConfig(),
      consent: this.$consent(),
      payment: this.$payment(),
      experiment: this.$experiment(),
      ...this.getLocalStorageState(),
      ...this.getSessionStorageState(),
    }
  }

  public persistToLocalStorage(): void {
    storageService.putJson(STATE, this.getLocalStorageState())
  }

  public persistToSessionStorage(): void {
    sessionStorageService.putJson(SESSION_STATE, this.getSessionStorageState())
  }

  /**
   * Returns a piece of state that is to be persisted.
   */
  private getLocalStorageState(): LocalStorageState {
    return {
      account: this.$account(),
      sessionId: this.$sessionId(),
      sessionEncryptionKeys: this.$sessionEncryptionKeys(),
      hardware: this.$hardware(),
      rawExperiment: this.$rawExperiment(),
      subscriptions: this.$subscriptions(),
      order: this.$order(),
      product: this.$product(),
      quiz: this.$quiz(),
      cart: this.$cart(),
      flow: this.$flow(),
    }
  }

  private getSessionStorageState(): SessionStorageState {
    return {
      discountCode: this.$discountCode(),
      referral: this.$referral(),
      qpFertilityGoal: this.$qpFertilityGoal(),
      isEmailVerificationFlow: this.$isEmailVerificationFlow(),
      overrideTime: this.$overrideTime(),
      overrideApiUrl: this.$overrideApiUrl(),
    }
  }

  private loadStateFromLocalStorage(): void {
    const {
      account,
      sessionId,
      sessionEncryptionKeys,
      hardware,
      rawExperiment,
      subscriptions,
      order,
      product,
      quiz,
      cart,
    } = storageService.getJson<LocalStorageState>(STATE) || ({} as LocalStorageState)

    this.$sessionId.set(sessionId)
    this.$sessionEncryptionKeys.set(sessionEncryptionKeys)
    if (account) {
      this.$account.set(account)
    }
    if (hardware) {
      this.$hardware.set(hardware)
    }
    if (rawExperiment) {
      this.$rawExperiment.set(rawExperiment)
    }
    if (subscriptions) {
      this.$subscriptions.set(subscriptions)
    }
    if (order) {
      this.$order.set(order)
    }
    if (product) {
      this.$product.set(product)
    }
    if (quiz) {
      this.$quiz.set(quiz)
    }
    if (cart) {
      this.$cart.set(cart)
    }
  }

  loadStateFromSessionStorage(): void {
    const {
      discountCode,
      referral,
      overrideTime,
      skippedDiscountedHardwareKey,

      overrideApiUrl,
    } = sessionStorageService.getJson<SessionStorageState>(SESSION_STATE) || {}
    if (discountCode) {
      this.$discountCode.set(discountCode)
    }
    if (referral) {
      this.$referral.set(referral)
    }
    if (overrideTime) {
      this.$overrideTime.set(overrideTime)
    }
    if (skippedDiscountedHardwareKey) {
      this.$skippedDiscountedHardwareKey.set(skippedDiscountedHardwareKey)
    }
    if (overrideApiUrl) {
      this.$overrideApiUrl.set(overrideApiUrl)
    }
  }

  replaceQuizFlow(flow: SignupQuizFlow): void {
    this.$quiz.update(s => ({
      ...s,
      flow: [...flow],
    }))
  }

  replaceQuizState(quizState: QuizState): void {
    this.$quiz.set(quizState)
  }

  updateQuizData(input: QuizDataInput): void {
    this.$quiz.update(s => ({
      ...s,
      data: {
        ...s.data,
        ...input,
      },
    }))
  }

  clearQuizState(): void {
    this.$quiz.set({
      flow: [],
      data: null,
      sections: null,
      signature: null,
      entry: null,
      exit: null,
    })
  }

  clearQuizData(): void {
    this.$quiz.update(s => ({
      ...s,
      data: null,
    }))
  }
}
